import { useCallback, useEffect, useRef, useState } from "react";

import { useServerSentEvent } from "Hooks/useServerSentEvent";
import { getLogger } from "Services/LoggingService";
import { useSseEchoMutation } from "Services/restApi/diagnosticsApi";

// Retest cadence. Also acts as the response deadline: a run that has not received a matching echo by
// the next tick (~5s later) is treated as failed. Keeping a single timer avoids a timeout/interval
// race and keeps the healthy/broken state deterministic.
const ECHO_INTERVAL_MS = 5000;

const logger = getLogger("SSE Diagnostic");

export type SseEchoStatus = "idle" | "checking" | "healthy" | "broken";

export type SseEchoDiagnostic = {
    status: SseEchoStatus;
    lastSuccessAt?: number;
    lastRoundTripMs?: number;
    runNow: () => void;
};

function makeNonce(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Runs the SSE echo round-trip diagnostic while `active` is true (the popover is open) and the tab is
 * visible. Each run POSTs a unique nonce to the API, which republishes it over the user's live SSE
 * stream; a matching nonce back before the next tick means the full pipeline is healthy. Once a result
 * is known the dot stays green or red until the next result, so only the very first run shows "checking".
 */
export function useSseEchoDiagnostic(active: boolean): SseEchoDiagnostic {
    const [sseEcho] = useSseEchoMutation();
    const [status, setStatus] = useState<SseEchoStatus>("idle");
    const [lastSuccessAt, setLastSuccessAt] = useState<number | undefined>(undefined);
    const [lastRoundTripMs, setLastRoundTripMs] = useState<number | undefined>(undefined);

    const pendingNonceRef = useRef<string | undefined>(undefined);
    const startedAtRef = useRef<number>(0);

    const runOnce = useCallback(() => {
        const nonce = makeNonce();
        const clientSentAt = Date.now();
        pendingNonceRef.current = nonce;
        startedAtRef.current = clientSentAt;
        // Only the first run shows the neutral "checking" state; later runs keep the last known
        // healthy/broken result until a new one is determined.
        setStatus((prev) => (prev === "idle" ? "checking" : prev));

        sseEcho({ nonce, clientSentAt })
            .unwrap()
            .catch((error) => {
                // A failed POST is itself a connectivity signal.
                if (pendingNonceRef.current === nonce) {
                    setStatus("broken");
                }
                logger.warn("SSE echo request failed", { error });
            });
    }, [sseEcho]);

    const tick = useCallback(() => {
        // The previous run never received a matching echo within the interval, so it failed.
        if (pendingNonceRef.current !== undefined) {
            setStatus("broken");
        }
        runOnce();
    }, [runOnce]);

    const handleEcho = useCallback((data: { nonce: string }) => {
        if (!pendingNonceRef.current || data.nonce !== pendingNonceRef.current) {
            return;
        }
        pendingNonceRef.current = undefined;
        setLastRoundTripMs(Date.now() - startedAtRef.current);
        setLastSuccessAt(Date.now());
        setStatus("healthy");
    }, []);

    useServerSentEvent("sse", "diagnostics.echo", handleEcho);

    useEffect(() => {
        if (!active) {
            pendingNonceRef.current = undefined;
            setStatus("idle");
            return;
        }

        let intervalId: ReturnType<typeof setInterval> | undefined;

        const start = () => {
            runOnce();
            intervalId = setInterval(tick, ECHO_INTERVAL_MS);
        };

        const stop = () => {
            if (intervalId !== undefined) {
                clearInterval(intervalId);
                intervalId = undefined;
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stop();
            } else if (intervalId === undefined) {
                start();
            }
        };

        if (!document.hidden) {
            start();
        }
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            stop();
            pendingNonceRef.current = undefined;
        };
    }, [active, runOnce, tick]);

    return { status, lastSuccessAt, lastRoundTripMs, runNow: runOnce };
}
